import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GoogleLogo, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export const AuthModal = ({ open, onOpenChange, initialMode = "login", onSuccess }) => {
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setMode(initialMode);
  }, [open, initialMode]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload =
        mode === "login"
          ? { email: form.email, password: form.password }
          : { name: form.name, email: form.email, password: form.password };
      const { data } = await api.post(endpoint, payload);
      setUser(data);
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      onOpenChange(false);
      if (onSuccess) onSuccess(data);
      else navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-sm border-zinc-200" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl font-extrabold tracking-tight">
            {mode === "login" ? "Welcome back" : "Join the club"}
          </DialogTitle>
        </DialogHeader>

        <button
          onClick={googleLogin}
          data-testid="google-login-btn"
          className="w-full flex items-center justify-center gap-3 border-2 border-zinc-200 rounded-sm py-3 font-bold hover:border-zinc-950 transition-colors"
        >
          <GoogleLogo size={20} weight="bold" /> Continue with Google
        </button>

        <div className="flex items-center gap-4 my-1">
          <div className="h-px bg-zinc-200 flex-1" />
          <span className="text-xs uppercase tracking-widest text-zinc-400">or</span>
          <div className="h-px bg-zinc-200 flex-1" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div className="space-y-1.5">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                data-testid="auth-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-sm"
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              data-testid="auth-email-input"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="rounded-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              data-testid="auth-password-input"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="rounded-sm"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="auth-submit-btn"
            className="w-full flex items-center justify-center gap-2 bg-[#FF4F00] text-white rounded-sm py-3 font-bold tracking-wide hover:bg-[#E04600] transition-colors disabled:opacity-60"
          >
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            <ArrowRight size={18} weight="bold" />
          </button>
        </form>

        <p className="text-sm text-center text-zinc-500">
          {mode === "login" ? "New here? " : "Already a member? "}
          <button
            data-testid="auth-toggle-mode"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="font-bold text-zinc-950 underline underline-offset-4"
          >
            {mode === "login" ? "Create an account" : "Sign in"}
          </button>
        </p>
      </DialogContent>
    </Dialog>
  );
};
